import cx from 'classnames';
import React, { cloneElement, ReactElement, ReactNode, useState } from 'react';
import { useId } from 'react-aria';
import { Arrow, useHover, useLayer } from 'react-laag';
import { PlacementType } from 'react-laag/dist/PlacementType';

type Props = {
  tooltip: ReactNode;
  background?: string;
  children: ReactElement;
  placement?: PlacementType;
  shouldShow?: boolean;
  childClassName?: string;
  tooltipClassName?: string;
  tooltipTag?: 'p' | 'div';
  className?: string;
};

export const Tooltip = ({
  tooltip,
  background,
  children,
  placement,
  shouldShow = true,
  tooltipClassName,
  childClassName,
  tooltipTag = 'p',
  className,
}: Props) => {
  const [isOver, hoverProps] = useHover({ delayEnter: 100, delayLeave: 300 });
  const [isFocused, setIsFocused] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const tooltipId = useId();
  const isOpen = shouldShow && !isDismissed && (isOver || isFocused);

  const { triggerProps, layerProps, renderLayer, arrowProps } = useLayer({
    isOpen,
    auto: true,
    placement: placement || 'bottom-center',
    triggerOffset: 8,
  });

  const TooltipTag = tooltipTag;

  return (
    <>
      {isOpen &&
        renderLayer(
          <div
            {...layerProps}
            {...hoverProps}
            className={cx('z-[2000]', className)}
            data-tooltip
          >
            <div
              role="tooltip"
              id={tooltipId}
              className={cx(
                background ? background : 'bg-gray-800',
                'rounded p-3 shadow-md max-w-lg items-center',
                tooltipClassName,
              )}
            >
              <TooltipTag className="font-inter text-xs text-white mb-0">
                {tooltip}
              </TooltipTag>
              <Arrow
                {...arrowProps}
                backgroundColor={background || '#2c3242'}
                size={5}
              />
            </div>
          </div>,
        )}
      <span
        {...triggerProps}
        {...hoverProps}
        onMouseEnter={event => {
          setIsDismissed(false);
          hoverProps.onMouseEnter(event);
        }}
        onFocus={() => {
          setIsFocused(true);
          setIsDismissed(false);
        }}
        onBlur={event => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setIsFocused(false);
          }
        }}
        className={childClassName}
      >
        {cloneElement(children, {
          onClick: (event: React.MouseEvent) => {
            setIsDismissed(false);
            children.props.onClick?.(event);
          },
          onKeyDown: (event: React.KeyboardEvent) => {
            if (isOpen && event.key === 'Escape') {
              setIsDismissed(true);
              event.stopPropagation();
              return;
            }
            children.props.onKeyDown?.(event);
          },
          'aria-describedby':
            [children.props['aria-describedby'], isOpen ? tooltipId : undefined]
              .filter(Boolean)
              .join(' ') || undefined,
        })}
      </span>
    </>
  );
};
